import React, { useMemo } from 'react'
import { Box, Typography, CircularProgress, Alert } from '@mui/material'
import { useMenu } from '../hooks/useMenu'
import { useAuth } from '../contexts/AuthContext'
import { BasicCard } from '../components/Basicas/BasicCard'
import ActionBar from '../components/ActionBar'
import { TelaResponse } from '../types/api'
import { Table } from 'lucide-react'

type TelaComChildren = TelaResponse & { children?: TelaComChildren[] }

/**
 * Encontra recursivamente uma tela pela rota no menu hierárquico
 */
function findTelaByRota(
  items: TelaComChildren[],
  rota: string
): TelaComChildren | null {
  const rotaNorm = rota.startsWith('/') ? rota : `/${rota}`
  for (const item of items) {
    const itemRota = item.rota?.startsWith('/') ? item.rota : `/${item.rota || ''}`
    if (itemRota === rotaNorm) return item
    if (item.children?.length) {
      const found = findTelaByRota(item.children, rota)
      if (found) return found
    }
  }
  return null
}

interface CardsFromMenuPageProps {
  /** Rota do grupo (ex: /basicas, /funcionais, /reservadas) */
  rota: string
  /** Título exibido no ActionBar (opcional, usa nome da tela se não informado) */
  title?: string
}

/**
 * Página que exibe cards para cada item filho do menu.
 * Usado em Tabelas Básicas, Tabelas Funcionais, Tabelas Reservadas.
 */
const CardsFromMenuPage: React.FC<CardsFromMenuPageProps> = ({ rota, title }) => {
  const { getEmpresaId, isSuperAdminMode } = useAuth()
  const empresaId = getEmpresaId()

  const { menuItems, isLoading } = useMenu(
    'GALATEA_WEB',
    !isSuperAdminMode() ? empresaId : undefined
  )

  const { telaPai, filhos } = useMemo(() => {
    if (!menuItems?.length) return { telaPai: null, filhos: [] as TelaComChildren[] }
    const pai = findTelaByRota(menuItems as TelaComChildren[], rota)
    if (!pai) return { telaPai: null, filhos: [] }
    const children = (pai.children || []).filter(
      (c) => c.ativo !== false && !c.deleted_at && c.rota
    )
    return { telaPai: pai, filhos: children }
  }, [menuItems, rota])

  const handleCardClick = (tela: TelaComChildren) => {
    const url = tela.rota?.startsWith('/') ? tela.rota : `/${tela.rota}`
    const fullUrl = `${window.location.origin}${url}`
    window.open(fullUrl, '_blank', 'noopener,noreferrer')
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  const displayTitle = title ?? telaPai?.nome ?? rota

  if (!telaPai) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Grupo não encontrado para a rota: {rota}
        </Alert>
      </Box>
    )
  }

  if (filhos.length === 0) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <ActionBar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mr: 'auto' }}>
            {displayTitle}
          </Typography>
        </ActionBar>
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhum item disponível neste grupo.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
      <ActionBar>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mr: 'auto' }}>
          {displayTitle}
        </Typography>
      </ActionBar>
      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-2">
        {filhos.map((child) => (
          <BasicCard
            key={child.id}
            title={child.nome}
            description={child.descricao || ''}
            color={child.card_color || undefined}
            icon={Table}
            onClick={() => handleCardClick(child)}
            square
            size="sm"
            filled
          />
        ))}
      </div>
    </Box>
  )
}

export default CardsFromMenuPage
